import type { DbExecutor } from "./executor";
import type { TaskRepository } from "./repositories/taskRepository";
import type { FinancingRepository } from "./repositories/financingRepository";
import type { CapitalRepository } from "./repositories/capitalRepository";
import type { CapitalPlanService, ForecastBridge } from "./capitalPlanService";
import type { CategoryBudgetService } from "./categoryBudgetService";
import { buildProjectPlan } from "../engine/capitalPlan";
import { addDays } from "../engine/financing";
import {
  DEFAULT_FORECAST_ASSUMPTIONS,
  buildExecutionForecast,
  buildPhases,
  observeForecast,
  planDraws,
  planPayments,
  synthesizeDraws,
  type ExecutionForecast,
  type ForecastAssumptions,
  type ForecastInputs,
  type ForecastObservation,
  type ForecastPhase,
  type PlannedDraw,
  type SynthesizedDraws,
} from "../engine/executionForecast";
import { taskBudgetCategory } from "../domain/taskTemplates";
import { resolveCategory } from "../engine/categoryBudget";
import { tradeById, type TradeId } from "../domain/trade";
import type { Cents } from "../domain/money";

/**
 * ASSEMBLING THE EXECUTION FORECAST (spec §75).
 *
 * This service gathers, in a bounded number of queries, everything the
 * pure forecast engine needs — the CPM schedule, the §74 category budget,
 * financing terms and recorded draws, payment tranches, planning
 * assumptions and any phase-timing experiment — and hands them over.
 *
 * It performs no arithmetic of its own. In particular it never computes
 * interest: the synthesized draws go into `buildProjectPlan`, whose
 * ledger produces every balance and payment, and the forecast reads its
 * explanations back out of that ledger.
 */

/** Everything the forecast decides before the loan ledger is built. */
interface PlannedState {
  now: string;
  inputs: ForecastInputs;
  phases: ForecastPhase[];
  planned: ReturnType<typeof planPayments>;
  synthesized: SynthesizedDraws;
  drawDetails: PlannedDraw[];
  schedule: { projectFinish: string | null };
  assumptionState: { values: ForecastAssumptions; isDefault: Record<string, boolean> };
  facilities: Array<{ id: string; name: string; interestRatePctX100: number | null }>;
  categories: Array<{
    categoryId: TradeId; displayName: string; budgetCents: Cents; actualCents: Cents;
    complete: boolean; basisSource: string;
  }>;
  settings: { minReserveCents: Cents };
  strategy: "flip" | "rental";
  shifts: Array<{ taskId: string; shiftDays: number }>;
  /** Which budget category each feature's work belongs to. */
  categoryOfFeature: Map<string, TradeId>;
}

export interface ForecastResult {
  forecast: ExecutionForecast;
  observations: ForecastObservation[];
  /** The §74 categories, so the UI can label and expand phases. */
  categories: Array<{ categoryId: TradeId; displayName: string; budgetCents: Cents; actualCents: Cents }>;
  assumptions: ForecastAssumptions;
  /** True when a phase-timing experiment is currently applied. */
  scenario: boolean;
  shifts: Array<{ taskId: string; shiftDays: number }>;
  strategy: "flip" | "rental";
  minReserveCents: Cents;
}

const today = (): string => new Date().toISOString().slice(0, 10);

export class ExecutionForecastService {
  constructor(
    private readonly db: DbExecutor,
    private readonly tasks: TaskRepository,
    private readonly financing: FinancingRepository,
    private readonly capital: CapitalRepository,
    private readonly capitalPlan: CapitalPlanService,
    private readonly categoryBudget: CategoryBudgetService,
  ) {}

  /* ---------------- planning assumptions (spec §75F) ---------------- */

  async assumptions(projectId: string): Promise<{ values: ForecastAssumptions; isDefault: Record<string, boolean> }> {
    const rows = await this.db.select<{
      draw_request_delay_days: number | null;
      draw_funding_days: number | null;
      listing_days: number | null;
      sale_closing_days: number | null;
    }>(
      "SELECT draw_request_delay_days, draw_funding_days, listing_days, sale_closing_days FROM forecast_assumptions WHERE project_id = ?",
      [projectId],
    );
    const row = rows[0];
    return {
      values: {
        drawRequestDelayDays: row?.draw_request_delay_days ?? DEFAULT_FORECAST_ASSUMPTIONS.drawRequestDelayDays,
        drawFundingDays: row?.draw_funding_days ?? DEFAULT_FORECAST_ASSUMPTIONS.drawFundingDays,
        listingDays: row?.listing_days ?? DEFAULT_FORECAST_ASSUMPTIONS.listingDays,
        saleClosingDays: row?.sale_closing_days ?? DEFAULT_FORECAST_ASSUMPTIONS.saleClosingDays,
      },
      isDefault: {
        drawRequestDelay: (row?.draw_request_delay_days ?? null) === null,
        drawFunding: (row?.draw_funding_days ?? null) === null,
        listing: (row?.listing_days ?? null) === null,
        saleClosing: (row?.sale_closing_days ?? null) === null,
      },
    };
  }

  async setAssumptions(projectId: string, values: Partial<ForecastAssumptions>): Promise<void> {
    const now = new Date().toISOString();
    const current = await this.assumptions(projectId);
    const merged = { ...current.values, ...values };
    const existing = await this.db.select<{ project_id: string }>(
      "SELECT project_id FROM forecast_assumptions WHERE project_id = ?",
      [projectId],
    );
    const params = [
      merged.drawRequestDelayDays, merged.drawFundingDays, merged.listingDays, merged.saleClosingDays, now,
    ];
    if (existing.length === 0) {
      await this.db.execute(
        `INSERT INTO forecast_assumptions (draw_request_delay_days, draw_funding_days, listing_days,
           sale_closing_days, updated_at, project_id) VALUES (?, ?, ?, ?, ?, ?)`,
        [...params, projectId],
      );
    } else {
      await this.db.execute(
        `UPDATE forecast_assumptions SET draw_request_delay_days = ?, draw_funding_days = ?,
           listing_days = ?, sale_closing_days = ?, updated_at = ? WHERE project_id = ?`,
        [...params, projectId],
      );
    }
  }

  /* ---------------- phase-timing experiments (spec §75O) ---------------- */

  async listShifts(projectId: string): Promise<Array<{ taskId: string; shiftDays: number }>> {
    const rows = await this.db.select<{ task_id: string; shift_days: number }>(
      "SELECT task_id, shift_days FROM forecast_task_shifts WHERE project_id = ?",
      [projectId],
    );
    return rows.map((r) => ({ taskId: r.task_id, shiftDays: Number(r.shift_days) }));
  }

  /**
   * Move a phase in the FORECAST only. The committed schedule is never
   * touched — that is what makes this safe to play with.
   */
  async shiftTask(projectId: string, taskId: string, shiftDays: number): Promise<void> {
    const now = new Date().toISOString();
    if (shiftDays === 0) {
      await this.db.execute("DELETE FROM forecast_task_shifts WHERE project_id = ? AND task_id = ?", [
        projectId, taskId,
      ]);
      return;
    }
    const existing = await this.db.select<{ task_id: string }>(
      "SELECT task_id FROM forecast_task_shifts WHERE project_id = ? AND task_id = ?",
      [projectId, taskId],
    );
    if (existing.length === 0) {
      await this.db.execute(
        "INSERT INTO forecast_task_shifts (project_id, task_id, shift_days, updated_at) VALUES (?, ?, ?, ?)",
        [projectId, taskId, shiftDays, now],
      );
    } else {
      await this.db.execute(
        "UPDATE forecast_task_shifts SET shift_days = ?, updated_at = ? WHERE project_id = ? AND task_id = ?",
        [shiftDays, now, projectId, taskId],
      );
    }
  }

  async clearShifts(projectId: string): Promise<void> {
    await this.db.execute("DELETE FROM forecast_task_shifts WHERE project_id = ?", [projectId]);
  }

  /**
   * PROMOTE the experiment (spec §75O): what the user was trying out
   * becomes the project's real schedule, through the ordinary task
   * repository, and the experiment is cleared. An explicit action —
   * nothing here happens as a side effect of looking at a forecast.
   */
  async applyShifts(projectId: string): Promise<number> {
    const shifts = await this.listShifts(projectId);
    if (shifts.length === 0) return 0;
    const schedule = await this.tasks.schedule(projectId);
    const tasks = await this.tasks.listByProject(projectId);
    let applied = 0;
    await this.db.transaction(async () => {
      for (const shift of shifts) {
        const task = tasks.find((t) => t.id === shift.taskId);
        const scheduled = schedule.byId.get(shift.taskId);
        if (!task || !scheduled) continue;
        // Pin the task to where the experiment put it. The CPM then
        // reschedules everything downstream, exactly as it always does.
        await this.tasks.update(shift.taskId, {
          name: task.name,
          category: task.category,
          notes: task.notes,
          durationDays: task.durationDays,
          plannedStart: addDays(scheduled.start, shift.shiftDays),
          featureId: task.featureId,
          assignee: task.assignee,
          durationSource: task.durationSource,
        } as never);
        applied += 1;
      }
      await this.db.execute("DELETE FROM forecast_task_shifts WHERE project_id = ?", [projectId]);
    });
    return applied;
  }

  /* ---------------- the forecast ---------------- */

  /**
   * The whole chronological forecast, in ONE call. Bounded: the query
   * count does not grow with the number of tasks, categories, draws or
   * events — a project with ten phases and a project with a hundred cost
   * lines issue the same statements (spec §75U).
   */
  async forecast(projectId: string): Promise<ForecastResult> {
    const state = await this.plannedState(projectId);
    return this.assemble(projectId, state);
  }

  /**
   * What the forecast tells the rest of the system about this project's
   * planning: when the lender is expected to send money, and when the
   * SCHEDULE finishes the work behind each feature (spec §75D/R). The
   * multi-project Capital Plan builds the same project on the same two
   * answers, so a draw can never arrive before the payment it reimburses.
   */
  async forecastBridge(projectId: string): Promise<ForecastBridge> {
    const state = await this.plannedState(projectId);
    return {
      draws: state.synthesized.draws,
      featureWorkFinish: featureWorkFinish(state),
      expectedExitDate: expectedExitDate(state),
    };
  }

  /** Everything decided before the loan ledger runs. */
  private async plannedState(projectId: string): Promise<PlannedState> {
    const now = today();
    const [schedule, taskRows, budget, facilities, existingDraws, paymentPlans, assumptionState, shifts, settings, strategy] =
      await Promise.all([
        this.tasks.schedule(projectId, now),
        this.db.select<{
          id: string; budget_category: string | null; feature_id: string | null; template_id: string | null;
        }>(
          "SELECT id, budget_category, feature_id, template_id FROM project_tasks WHERE project_id = ? AND archived_at IS NULL",
          [projectId],
        ),
        this.categoryBudget.model(projectId),
        this.financing.listFacilities(projectId),
        this.financing.listDraws(projectId),
        this.financing.listPaymentPlans(projectId),
        this.assumptions(projectId),
        this.listShifts(projectId),
        this.capital.getPlanSettings(),
        this.capitalPlan.strategyOf(projectId),
      ]);

    /* ---- which budget category each scheduled task spends against ---- */
    const featureRows = await this.db.select<{
      id: string; origin_id: string | null; feature_definition_id: string | null; action: string;
    }>(
      "SELECT id, origin_id, feature_definition_id, action FROM project_features WHERE project_id = ? AND archived_at IS NULL",
      [projectId],
    );
    const decisionRows = await this.db.select<{ root_id: string; trade_override: string | null }>(
      "SELECT root_id, trade_override FROM bid_decisions WHERE project_id = ?",
      [projectId],
    );
    const decisions = new Map<string, TradeId>();
    for (const row of decisionRows) {
      if (row.trade_override) decisions.set(row.root_id, row.trade_override as TradeId);
    }
    const categoryOfFeature = new Map<string, TradeId>();
    for (const feature of featureRows) {
      categoryOfFeature.set(
        feature.id,
        resolveCategory(
          {
            rootId: feature.origin_id ?? feature.id,
            featureDefinitionId: feature.feature_definition_id,
            action: feature.action,
          },
          decisions,
          new Map(),
        ),
      );
    }
    const taskMeta = new Map(taskRows.map((r) => [r.id, r]));
    const shiftOf = new Map(shifts.map((s) => [s.taskId, s.shiftDays]));

    const forecastTasks = schedule.tasks.map((task) => {
      const meta = taskMeta.get(task.id);
      const shiftDays = shiftOf.get(task.id) ?? 0;
      return {
        id: task.id,
        name: task.name,
        // A phase-timing experiment moves a COPY of the dates.
        start: shiftDays === 0 ? task.start : addDays(task.start, shiftDays),
        finish: shiftDays === 0 ? task.finish : addDays(task.finish, shiftDays),
        categoryId: taskBudgetCategory({
          budgetCategory: meta?.budget_category ?? null,
          featureCategory: meta?.feature_id ? (categoryOfFeature.get(meta.feature_id) ?? null) : null,
          templateId: meta?.template_id ?? null,
        }),
        complete: task.status === "complete",
        fromActuals: task.fromActuals,
        milestone: task.milestone,
      };
    });

    const categories = budget.rows
      .filter((row) => row.categoryId !== "unassigned-money" && !row.categoryId.includes("+"))
      .map((row) => ({
        categoryId: row.categoryId as TradeId,
        displayName: row.displayName,
        budgetCents: row.currentBasisCents,
        actualCents: row.actualCents,
        remainingCents: row.remainingCents,
        complete: row.complete,
        basisSource: row.basisSource,
      }));

    // One rehab facility drives the draw chain: the one that HAS rehab
    // money. Several is legal and each keeps its own ledger, but the
    // forecast never guesses which one a draw would come from.
    const rehabFacility =
      facilities.find((f) => f.loanType !== "refinance" && (f.rehabFacilityCents ?? 0) > 0) ?? null;

    const inputs: ForecastInputs = {
      tasks: forecastTasks,
      categories,
      tranches: paymentPlans
        .filter((p) => p.sourceKind === "category" && p.sourceId)
        .map((p) => ({
          id: p.id,
          categoryId: p.sourceId as TradeId,
          label: p.label,
          pctX100: p.pctX100,
          amountCents: p.amountCents,
          anchor:
            p.anchor === "task-start" || p.anchor === "project-start"
              ? ("phase-start" as const)
              : p.anchor === "date"
                ? ("date" as const)
                : ("phase-finish" as const),
          date: p.date,
          offsetDays: p.offsetDays,
        })),
      facility: rehabFacility
        ? {
            id: rehabFacility.id,
            name: rehabFacility.name,
            rehabReimbursement: rehabFacility.rehabReimbursement,
            rehabFacilityCents: rehabFacility.rehabFacilityCents,
            minimumDrawCents: rehabFacility.minimumDrawCents,
            drawFeeCents: rehabFacility.drawFeeCents,
            rehabFundingPctX100: rehabFacility.rehabFundingPctX100,
          }
        : null,
      existingDraws,
      assumptions: assumptionState.values,
      assumptionsAreDefault: {
        drawRequestDelay: assumptionState.isDefault.drawRequestDelay,
        drawFunding: assumptionState.isDefault.drawFunding,
      },
      today: now,
    };

    /* ---- phases → payments → draws → the real ledger ---- */
    const phases = buildPhases(inputs);
    const planned = planPayments(phases, inputs);
    const synthesized = synthesizeDraws(planned.payments, inputs);
    const drawDetails = planDraws(planned.payments, inputs).draws;

    return {
      now, inputs, phases, planned, synthesized, drawDetails, schedule, assumptionState,
      facilities, categories, settings, strategy, shifts, categoryOfFeature,
    };
  }

  /** The ledger, the chronology and everything read back off it. */
  private async assemble(projectId: string, state: PlannedState): Promise<ForecastResult> {
    const {
      inputs, phases, planned, synthesized, drawDetails, schedule, assumptionState,
      facilities, categories, settings, strategy, shifts,
    } = state;

    // The plan is built with the forecast's draws ALONGSIDE the recorded
    // ones, so its ledger sees the balance the project will really have.
    const planInputs = await this.capitalPlan.projectInputs(
      projectId,
      this.horizonFor(schedule, assumptionState.values),
      // This path adds the synthesized draws itself, below.
      { synthesizeDraws: false },
    );
    const plan = buildProjectPlan({
      ...planInputs,
      draws: [...planInputs.draws, ...synthesized.draws],
      featureWorkFinish: featureWorkFinish(state),
      expectedExitDate: expectedExitDate(state),
    });

    const forecast = buildExecutionForecast(plan, inputs, {
      phases,
      plannedDraws: drawDetails,
      facilityNames: new Map(facilities.map((f) => [f.id, f.name])),
      facilityRates: new Map(facilities.map((f) => [f.id, f.interestRatePctX100])),
      minReserveCents: settings.minReserveCents,
      startingCashCents: 0,
      scenario: shifts.length > 0,
      extraAssumptions: [...planned.assumptions, ...synthesized.assumptions],
    });

    return {
      forecast,
      observations: observeForecast(forecast, settings.minReserveCents),
      categories: categories.map((c) => ({
        categoryId: c.categoryId,
        displayName: c.displayName,
        budgetCents: c.budgetCents,
        actualCents: c.actualCents,
      })),
      assumptions: assumptionState.values,
      scenario: shifts.length > 0,
      shifts,
      strategy,
      minReserveCents: settings.minReserveCents,
    };
  }

  /** Far enough out to contain the exit and a rental's stabilisation. */
  private horizonFor(
    schedule: { projectFinish: string | null },
    assumptions: ForecastAssumptions,
  ): string {
    const base = schedule.projectFinish ?? today();
    const afterExit = addDays(base, assumptions.listingDays + assumptions.saleClosingDays + 365);
    return afterExit > today() ? afterExit : addDays(today(), 365);
  }

  /**
   * Which task categories a project's schedule currently maps to, for the
   * mapping UI. Bounded: one query.
   */
  async taskCategoryMap(
    projectId: string,
  ): Promise<Array<{ taskId: string; name: string; explicit: string | null; resolved: TradeId | null; source: string }>> {
    const [taskRows, featureRows, decisionRows] = await Promise.all([
      this.db.select<{
        id: string; name: string; budget_category: string | null; feature_id: string | null;
        template_id: string | null; sort_order: number;
      }>(
        "SELECT id, name, budget_category, feature_id, template_id, sort_order FROM project_tasks WHERE project_id = ? AND archived_at IS NULL ORDER BY sort_order, name",
        [projectId],
      ),
      this.db.select<{ id: string; origin_id: string | null; feature_definition_id: string | null; action: string }>(
        "SELECT id, origin_id, feature_definition_id, action FROM project_features WHERE project_id = ? AND archived_at IS NULL",
        [projectId],
      ),
      this.db.select<{ root_id: string; trade_override: string | null }>(
        "SELECT root_id, trade_override FROM bid_decisions WHERE project_id = ?",
        [projectId],
      ),
    ]);
    const decisions = new Map<string, TradeId>();
    for (const row of decisionRows) {
      if (row.trade_override) decisions.set(row.root_id, row.trade_override as TradeId);
    }
    const categoryOfFeature = new Map<string, TradeId>();
    for (const feature of featureRows) {
      categoryOfFeature.set(
        feature.id,
        resolveCategory(
          { rootId: feature.origin_id ?? feature.id, featureDefinitionId: feature.feature_definition_id, action: feature.action },
          decisions,
          new Map(),
        ),
      );
    }
    return taskRows.map((row) => {
      const featureCategory = row.feature_id ? (categoryOfFeature.get(row.feature_id) ?? null) : null;
      const resolved = taskBudgetCategory({
        budgetCategory: row.budget_category,
        featureCategory,
        templateId: row.template_id,
      });
      const source =
        row.budget_category && tradeById(row.budget_category)
          ? "you set this"
          : featureCategory
            ? "from the linked work item"
            : resolved
              ? "from the task template"
              : "no renovation budget";
      return { taskId: row.id, name: row.name, explicit: row.budget_category, resolved, source };
    });
  }

  /** Say what a task spends against. A decision — never inferred later. */
  async setTaskCategory(taskId: string, categoryId: TradeId | null): Promise<void> {
    await this.db.execute(
      "UPDATE project_tasks SET budget_category = ?, updated_at = ? WHERE id = ?",
      [categoryId, new Date().toISOString(), taskId],
    );
  }
}

/**
 * When the schedule finishes the work behind each feature (spec §75D).
 *
 * A task is not a feature: scope-generated tasks carry no feature link, so
 * without this the plan would pay every feature at the project finish while
 * the forecast completed its phases weeks earlier — and a reimbursement
 * draw would arrive before the payment it reimburses. The bridge is the
 * SAME category mapping both sides already use; a feature whose category
 * nobody scheduled is deliberately absent, so the old default stands.
 */
function featureWorkFinish(state: PlannedState): Map<string, string> {
  const finishOfCategory = new Map<string, string>();
  for (const phase of state.phases) {
    if (phase.categoryId === null) continue;
    const current = finishOfCategory.get(phase.categoryId);
    if (current === undefined || phase.finish > current) finishOfCategory.set(phase.categoryId, phase.finish);
  }
  const dates = new Map<string, string>();
  for (const [featureId, categoryId] of state.categoryOfFeature) {
    const finish = finishOfCategory.get(categoryId);
    if (finish) dates.set(featureId, finish);
  }
  return dates;
}

/**
 * When a projected sale is expected to CLOSE (spec §75B, flips only).
 *
 * Finishing the work, listing the house and reaching the closing table are
 * three different days, and the difference is interest and holding the
 * owner really pays. Both assumptions are named and editable, and a sale
 * date somebody actually recorded always wins inside the plan engine.
 */
function expectedExitDate(state: PlannedState): string | null {
  if (state.strategy !== "flip") return null;
  const finish = state.schedule.projectFinish;
  if (!finish) return null;
  return addDays(finish, state.assumptionState.values.listingDays + state.assumptionState.values.saleClosingDays);
}
